import React, { useMemo, useState } from "react";
import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import AdminQuickLinks from "./AdminQuickLinks";
import "./AllAddMatchesFilter.css";

const TIMEZONE = "Africa/Dar_es_Salaam";
const MATCHES_COLLECTION = "matches";

/*
 * Login compatibility:
 * - Prefer a real Firebase Auth session when one exists.
 * - Otherwise, check the existing app login stored by Login.jsx.
 *   Login.jsx writes: authenticated, user, username, and role to localStorage.
 */
function getAppLoginUser() {
  if (auth.currentUser) {
    return {
      uid: auth.currentUser.uid,
      username: auth.currentUser.displayName || "",
      source: "firebase",
    };
  }

  try {
    const authenticated =
      localStorage.getItem("authenticated") === "true";
    if (!authenticated) return null;

    const rawUser = localStorage.getItem("user");
    let savedUser = {};
    if (rawUser) {
      try {
        savedUser = JSON.parse(rawUser) || {};
      } catch {
        savedUser = {};
      }
    }

    const username =
      savedUser.username ||
      localStorage.getItem("username") ||
      "";

    return {
      ...savedUser,
      uid: savedUser.uid || savedUser.id || username,
      username,
      source: "localStorage",
    };
  } catch (error) {
    console.warn("Could not read app login state:", error);
    return null;
  }
}

/* =========================================================
   DEFAULT CORRECT SCORES
========================================================= */
const defaultScoreOdds = [
  { score: "0-0", odds: 0.9 },
  { score: "1-0", odds: 0.5 },
  { score: "0-1", odds: 5.0 },
  { score: "1-1", odds: 4.8 },
  { score: "2-0", odds: 6.5 },
  { score: "0-2", odds: 5.8 },
  { score: "2-1", odds: 1.8 },
  { score: "1-2", odds: 4.7 },
  { score: "2-2", odds: 0.5 },
  { score: "3-0", odds: 0.3 },
  { score: "0-3", odds: 4.6 },
  { score: "3-1", odds: 0.7 },
  { score: "1-3", odds: 4.7 },
  { score: "3-2", odds: 0.4 },
  { score: "2-3", odds: 0.6 },
  { score: "3-3", odds: 0.7 },
];
/* =========================================================
   DATE HELPERS
========================================================= */

function formatDateEAT(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const year =
    parts.find((part) => part.type === "year")?.value || "";

  const month =
    parts.find((part) => part.type === "month")?.value || "";

  const day =
    parts.find((part) => part.type === "day")?.value || "";

  return `${year}-${month}-${day}`;
}

function getToday() {
  return formatDateEAT(new Date());
}

function getTomorrow() {
  const today = getToday();

  const [year, month, day] = today.split("-").map(Number);

  const tomorrow = new Date(
    Date.UTC(year, month - 1, day + 1, 12, 0, 0)
  );

  return tomorrow.toISOString().slice(0, 10);
}

function isValidISODate(dateString) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return false;
  }

  const [year, month, day] = dateString.split("-").map(Number);

  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/* =========================================================
   TIME
========================================================= */


function normaliseTime(value) {
  if (!value) return "";

  const clean = String(value)
    .trim()
    .replace(/\s+/g, " ")
    // Ondoa event ID kama #261 au #1234 mwisho wa muda
    .replace(/#\s*\d+\s*$/, "")
    .trim();

  let match = clean.match(/^(\d{1,2}):(\d{2})$/);

  if (match) {
    const hour = Number(match[1]);
    const minute = Number(match[2]);

    if (
      hour >= 0 &&
      hour <= 23 &&
      minute >= 0 &&
      minute <= 59
    ) {
      return `${String(hour).padStart(2, "0")}:${String(
        minute
      ).padStart(2, "0")}`;
    }
  }

  match = clean.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

  if (match) {
    let hour = Number(match[1]);
    const minute = Number(match[2]);
    const period = match[3].toUpperCase();

    if (
      hour < 1 ||
      hour > 12 ||
      minute < 0 ||
      minute > 59
    ) {
      return "";
    }

    if (period === "AM" && hour === 12) {
      hour = 0;
    }

    if (period === "PM" && hour !== 12) {
      hour += 12;
    }

    return `${String(hour).padStart(2, "0")}:${String(
      minute
    ).padStart(2, "0")}`;
  }

  return "";
}

/* =========================================================
   DATE + TIME
========================================================= */

const DATE_TIME_RE =
  /^(Today|Tomorrow|\d{1,2}\/\d{1,2}\/\d{2,4}|\d{4}-\d{1,2}-\d{1,2})\s+(.+)$/i;

function parseDateTimeLine(line) {
  if (!line) return null;

  const clean = String(line)
    .replace(/\s+/g, " ")
    .trim();

  const match = clean.match(DATE_TIME_RE);

  if (!match) return null;

  const datePart = match[1];
  const timePart = match[2];

  const time = normaliseTime(timePart);

  if (!time) return null;

  let date = "";

  if (/^today$/i.test(datePart)) {
    date = getToday();
  } else if (/^tomorrow$/i.test(datePart)) {
    date = getTomorrow();
  } else if (/^\d{4}-/.test(datePart)) {
    const parts = datePart.split("-");

    if (parts.length !== 3) {
      return null;
    }

    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);

    const candidate = `${String(year).padStart(
      4,
      "0"
    )}-${String(month).padStart(2, "0")}-${String(day).padStart(
      2,
      "0"
    )}`;

    if (!isValidISODate(candidate)) {
      return null;
    }

    date = candidate;
  } else {
    const parts = datePart.split("/");

    if (parts.length !== 3) {
      return null;
    }

    const day = Number(parts[0]);
    const month = Number(parts[1]);
    let year = Number(parts[2]);

    if (year >= 0 && year < 100) {
      year += 2000;
    }

    const candidate = `${String(year).padStart(
      4,
      "0"
    )}-${String(month).padStart(2, "0")}-${String(day).padStart(
      2,
      "0"
    )}`;

    if (!isValidISODate(candidate)) {
      return null;
    }

    date = candidate;
  }

  return {
    date,
    time,
  };
}

/* =========================================================
   NUMBER
========================================================= */

function safeNumber(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const number = Number(
    String(value)
      .replace(",", ".")
      .trim()
  );

  return Number.isFinite(number) ? number : null;
}

/* =========================================================
   COMPACT ODDS

   2.723.452.15
   => 2.72 / 3.45 / 2.15
========================================================= */

const COMPACT_ODDS_RE =
  /^(\d+\.\d{2})(\d+\.\d{2})(\d+\.\d{2})$/;

function parseCompactOdds(line) {
  if (!line) return null;

  const clean = String(line)
    .replace(/\s+/g, "")
    .trim();

  const match = clean.match(COMPACT_ODDS_RE);

  if (!match) return null;

  const home = safeNumber(match[1]);
  const draw = safeNumber(match[2]);
  const away = safeNumber(match[3]);

  if (
    home === null ||
    draw === null ||
    away === null
  ) {
    return null;
  }

  return {
    home,
    draw,
    away,
  };
}

/* =========================================================
   SEPARATE ODDS
========================================================= */

function parseSeparateOdds(lines) {
  if (!Array.isArray(lines)) {
    return null;
  }

  const text = lines
    .map((item) => String(item).trim())
    .filter(Boolean)
    .join(" ");

  let match = text.match(
    /\b1\s+(\d+(?:\.\d+)?)\s+X\s+(\d+(?:\.\d+)?)\s+2\s+(\d+(?:\.\d+)?)/i
  );

  if (match) {
    return {
      home: safeNumber(match[1]),
      draw: safeNumber(match[2]),
      away: safeNumber(match[3]),
    };
  }

  const values = text.match(/\d+(?:\.\d+)?/g);

  if (values && values.length === 3) {
    return {
      home: safeNumber(values[0]),
      draw: safeNumber(values[1]),
      away: safeNumber(values[2]),
    };
  }

  return null;
}

/* =========================================================
   LEAGUE DETECTION
========================================================= */

function isLeagueCandidate(line) {
  if (!line) return false;

  const clean = String(line)
    .replace(/\s+/g, " ")
    .trim();

  if (!clean) return false;

  if (/\s-\s/.test(clean)) {
    return true;
  }

  const knownLeagues = [
    "Premier League",
    "LaLiga",
    "La Liga",
    "Serie A",
    "Serie B",
    "Bundesliga",
    "Ligue 1",
    "Ligue 2",
    "Champions League",
    "Europa League",
    "Conference League",
    "FA Cup",
    "Copa del Rey",
    "Coppa Italia",
    "Eredivisie",
    "Primeira Liga",
    "Super Lig",
    "MLS",
  ];

  return knownLeagues.some(
    (league) =>
      clean.toLowerCase() === league.toLowerCase()
  );
}

/* =========================================================
   META LINE
========================================================= */

function isMetaLine(line) {
  if (!line) return true;

  const clean = String(line)
    .replace(/\s+/g, " ")
    .trim();

  if (!clean) return true;

  if (/^\+\d+$/.test(clean)) {
    return true;
  }

  if (/^\d+$/.test(clean)) {
    return true;
  }

  if (
    /^(By Time|Highlights|Upcoming Matches|1X2|Home|Draw|Away)$/i.test(
      clean
    )
  ) {
    return true;
  }

  if (/^[12X]$/i.test(clean)) {
    return true;
  }

  if (/^(v|vs|versus)$/i.test(clean)) {
    return true;
  }

  if (/^\d{1,2}:\d{2}$/.test(clean)) {
    return true;
  }

  return false;
}

/* =========================================================
   LEAGUE PARSER
========================================================= */

function parseLeague(leagueLine) {
  const clean = String(leagueLine || "")
    .replace(/\s+/g, " ")
    .trim();

  if (!clean) {
    return {
      country: "",
      league: "",
    };
  }

  const parts = clean.split(/\s+-\s+/);

  if (parts.length >= 2) {
    return {
      country: parts[0].trim(),
      league: parts.slice(1).join(" - ").trim(),
    };
  }

  return {
    country: "",
    league: clean,
  };
}

/* =========================================================
   CORRECT SCORES
========================================================= */

function getCorrectScores() {
  return defaultScoreOdds.map((item) => ({
    score: item.score,
    odds: item.odds,
  }));
}

/* =========================================================
   MATCH OBJECT
========================================================= */

function buildMatch({
  leagueLine,
  teamA,
  teamB,
  dateTime,
  odds,
}) {
  if (
    !leagueLine ||
    !teamA ||
    !teamB ||
    !dateTime
  ) {
    return null;
  }

  const A = String(teamA).trim();
  const B = String(teamB).trim();

  if (!A || !B) {
    return null;
  }

  if (
    isMetaLine(A) ||
    isMetaLine(B)
  ) {
    return null;
  }

  if (
    /^(v|vs|versus)$/i.test(A) ||
    /^(v|vs|versus)$/i.test(B)
  ) {
    return null;
  }

  if (
    !dateTime.date ||
    !dateTime.time
  ) {
    return null;
  }

  const {
    country,
    league,
  } = parseLeague(leagueLine);

  return {
    A,
    B,

    date: dateTime.date,
    time: dateTime.time,

    country,
    league,

    timezone: TIMEZONE,

    logoA: "",
    logoB: "",
    leagueLogo: "",

    odds: {
      home: odds?.home ?? null,
      draw: odds?.draw ?? null,
      away: odds?.away ?? null,
    },

    correctScores: getCorrectScores(),

    source: "manual",
  };
}

/* =========================================================
   LEAGUE BLOCK
========================================================= */

function parseLeagueBlock(leagueLine, block) {
  const lines = Array.isArray(block)
    ? block
        .map((line) =>
          String(line)
            .replace(/\u00a0/g, " ")
            .replace(/\t/g, " ")
            .replace(/\s+/g, " ")
            .trim()
        )
        .filter(Boolean)
    : [];

  if (!lines.length) {
    return null;
  }

  let teamA = "";
  let teamB = "";
  let dateTime = null;
  let odds = null;

  for (const line of lines) {
    const dt = parseDateTimeLine(line);

    if (dt) {
      dateTime = dt;
      continue;
    }

    const compact = parseCompactOdds(line);

    if (compact) {
      odds = compact;
    }
  }

  if (!odds) {
    odds = parseSeparateOdds(lines);
  }

  const vIndex = lines.findIndex((line) =>
    /^(v|vs|versus)$/i.test(line)
  );

  if (
    vIndex > 0 &&
    vIndex + 1 < lines.length
  ) {
    const possibleA = lines[vIndex - 1];
    const possibleB = lines[vIndex + 1];

    if (
      !isMetaLine(possibleA) &&
      !isMetaLine(possibleB) &&
      !parseDateTimeLine(possibleA) &&
      !parseDateTimeLine(possibleB) &&
      !parseCompactOdds(possibleA) &&
      !parseCompactOdds(possibleB)
    ) {
      teamA = possibleA;
      teamB = possibleB;
    }
  }

  if (!teamA || !teamB) {
    const teamCandidates = lines.filter((line) => {
      if (isMetaLine(line)) return false;
      if (parseDateTimeLine(line)) return false;
      if (parseCompactOdds(line)) return false;
      if (isLeagueCandidate(line)) return false;

      return true;
    });

    if (teamCandidates.length >= 2) {
      teamA = teamCandidates[0];
      teamB = teamCandidates[1];
    }
  }

  if (
    !teamA ||
    !teamB ||
    !dateTime
  ) {
    return null;
  }

  return buildMatch({
    leagueLine,
    teamA,
    teamB,
    dateTime,
    odds,
  });
}

/* =========================================================
   BETTING TEXT PARSER
========================================================= */

function parseBettingText(rawText) {
  if (
    !rawText ||
    !String(rawText).trim()
  ) {
    return [];
  }

  const lines = String(rawText)
    .replace(/\r/g, "")
    .replace(/\u00a0/g, " ")
    .split("\n")
    .map((line) =>
      line
        .replace(/\t/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    )
    .filter(Boolean);

  const matches = [];

  for (
    let i = 0;
    i < lines.length;
    i++
  ) {
    if (!isLeagueCandidate(lines[i])) {
      continue;
    }

    const leagueLine = lines[i];

    let end = lines.length;

    for (
      let j = i + 1;
      j < lines.length;
      j++
    ) {
      if (isLeagueCandidate(lines[j])) {
        end = j;
        break;
      }
    }

    const block = lines.slice(
      i + 1,
      end
    );

    const match = parseLeagueBlock(
      leagueLine,
      block
    );

    if (match) {
      matches.push(match);
    }

    i = end - 1;
  }

  return matches;
}

/* =========================================================
   JSON CLEANER
========================================================= */

function cleanJsonInput(input) {
  let clean = String(input || "").trim();

  clean = clean.replace(
    /^```(?:json)?\s*/i,
    ""
  );

  clean = clean.replace(
    /\s*```$/i,
    ""
  );

  return clean.trim();
}

/* =========================================================
   JSON NORMALISER
========================================================= */

function normaliseJsonMatch(item) {
  if (
    !item ||
    typeof item !== "object"
  ) {
    return null;
  }

  const A =
    item.A ??
    item.homeTeam ??
    item.home ??
    item.teamA ??
    "";

  const B =
    item.B ??
    item.awayTeam ??
    item.away ??
    item.teamB ??
    "";

  if (
    !String(A).trim() ||
    !String(B).trim()
  ) {
    return null;
  }

  let date =
    item.date ??
    item.matchDate ??
    "";

  let time =
    item.time ??
    item.matchTime ??
    "";

  date = String(date || "").trim();
  time = String(time || "").trim();

  if (!date || !time) {
    const combined =
      item.datetime ??
      item.dateTime ??
      item.kickoff ??
      item.startTime ??
      "";

    if (combined) {
      const parsed = parseDateTimeLine(
        String(combined).replace("T", " ")
      );

      if (parsed) {
        date = parsed.date;
        time = parsed.time;
      }
    }
  }

  if (date && time) {
    const parsed = parseDateTimeLine(
      `${date} ${time}`
    );

    if (parsed) {
      date = parsed.date;
      time = parsed.time;
    }
  }

  if (/^today$/i.test(date)) {
    date = getToday();
  }

  if (/^tomorrow$/i.test(date)) {
    date = getTomorrow();
  }

  time = normaliseTime(time);

  if (!date && time) {
    date = getToday();
  }

  if (
    date &&
    /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(date)
  ) {
    const parts = date.split("/");

    const day = Number(parts[0]);
    const month = Number(parts[1]);
    let year = Number(parts[2]);

    if (year < 100) {
      year += 2000;
    }

    date = `${String(year).padStart(
      4,
      "0"
    )}-${String(month).padStart(
      2,
      "0"
    )}-${String(day).padStart(
      2,
      "0"
    )}`;
  }

  if (
    !date ||
    !isValidISODate(date) ||
    !time
  ) {
    return null;
  }

  const odds =
    item.odds &&
    typeof item.odds === "object"
      ? item.odds
      : {};

  const home =
    odds.home ??
    odds["1"] ??
    item.homeOdds ??
    item.oddHome ??
    null;

  const draw =
    odds.draw ??
    odds.X ??
    odds.x ??
    item.drawOdds ??
    item.oddDraw ??
    null;

  const away =
    odds.away ??
    odds["2"] ??
    item.awayOdds ??
    item.oddAway ??
    null;

  const correctScores =
    Array.isArray(item.correctScores)
      ? item.correctScores.map(
          (score) => ({
            score:
              score.score ?? "",
            odds:
              safeNumber(score.odds),
          })
        )
      : getCorrectScores();

  return {
    A: String(A).trim(),

    B: String(B).trim(),

    date,

    time,

    country: String(
      item.country ?? "Other"
    ).trim(),

    league: String(
      item.league ??
        item.competition ??
        "Unknown League"
    ).trim(),

    timezone:
      item.timezone ??
      TIMEZONE,

    logoA: item.logoA ?? "",

    logoB: item.logoB ?? "",

    leagueLogo:
      item.leagueLogo ?? "",

    odds: {
      home: safeNumber(home),
      draw: safeNumber(draw),
      away: safeNumber(away),
    },

    correctScores,

    source: "manual",
  };
}

/* =========================================================
   JSON PARSER
========================================================= */

function tryParseJSON(input) {
  try {
    const clean = cleanJsonInput(input);

    if (!clean) {
      return null;
    }

    const parsed = JSON.parse(clean);

    if (Array.isArray(parsed)) {
      return parsed
        .map(normaliseJsonMatch)
        .filter(Boolean);
    }

    if (
      parsed &&
      Array.isArray(parsed.matches)
    ) {
      return parsed.matches
        .map(normaliseJsonMatch)
        .filter(Boolean);
    }

    const single =
      normaliseJsonMatch(parsed);

    return single ? [single] : [];
  } catch (error) {
    console.warn(
      "JSON parsing failed:",
      error
    );

    return null;
  }
}

/* =========================================================
   MATCH KEY
========================================================= */

function matchKey(match) {
  return [
    String(match?.A || "")
      .trim()
      .toLowerCase(),

    String(match?.B || "")
      .trim()
      .toLowerCase(),

    String(match?.date || "").trim(),

    String(match?.time || "").trim(),

    String(match?.league || "")
      .trim()
      .toLowerCase(),
  ].join("|");
}

/* =========================================================
   COMPONENT
========================================================= */

export default function AllAddMatches() {
  const [
    manualText,
    setManualText,
  ] = useState("");

  const [
    matches,
    setMatches,
  ] = useState([]);

  const [
    generating,
    setGenerating,
  ] = useState(false);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    countryFilter,
    setCountryFilter,
  ] = useState("all");

  const [
    leagueFilter,
    setLeagueFilter,
  ] = useState("all");

  const [
    dateFilter,
    setDateFilter,
  ] = useState("");

  const [
    timeFilter,
    setTimeFilter,
  ] = useState("");

  /* =======================================================
     COUNTRIES
  ======================================================= */

  const countries = useMemo(() => {
    const values = matches
      .map((match) => match.country)
      .filter(Boolean);

    return [
      "all",
      ...Array.from(
        new Set(values)
      ).sort(),
    ];
  }, [matches]);

  /* =======================================================
     LEAGUES
  ======================================================= */

  const leagues = useMemo(() => {
    const values = matches
      .filter(
        (match) =>
          countryFilter === "all" ||
          match.country === countryFilter
      )
      .map((match) => match.league)
      .filter(Boolean);

    return [
      "all",
      ...Array.from(
        new Set(values)
      ).sort(),
    ];
  }, [
    matches,
    countryFilter,
  ]);

  /* =======================================================
     FILTERED MATCHES
  ======================================================= */

  const filteredMatches = useMemo(() => {
    const q = search.trim().toLowerCase();

    return matches.filter((match) => {
      const searchOK =
        !q ||
        [
          match.A,
          match.B,
          match.country,
          match.league,
        ]
          .join(" ")
          .toLowerCase()
          .includes(q);

      const countryOK =
        countryFilter === "all" ||
        match.country === countryFilter;

      const leagueOK =
        leagueFilter === "all" ||
        match.league === leagueFilter;

      const dateOK =
        !dateFilter ||
        match.date === dateFilter;

      const timeOK =
        !timeFilter ||
        match.time === timeFilter;

      return (
        searchOK &&
        countryOK &&
        leagueOK &&
        dateOK &&
        timeOK
      );
    });
  }, [
    matches,
    search,
    countryFilter,
    leagueFilter,
    dateFilter,
    timeFilter,
  ]);

  /* =======================================================
     GENERATE MATCHES
  ======================================================= */

  function parseAllMatches() {
    setGenerating(true);

    try {
      const raw = manualText.trim();

      if (!raw) {
        alert(
          "Paste betting data kwanza."
        );
        return;
      }

      let parsed = [];

      const jsonResult =
        tryParseJSON(raw);

      if (
        jsonResult &&
        jsonResult.length
      ) {
        parsed = jsonResult;
      } else {
        parsed = parseBettingText(raw);
      }

      if (!parsed.length) {
        alert(
          "Hakuna matches zilizopatikana.\n\n" +
            "Hakikisha format ina League, Teams, Odds na Date/Time."
        );

        return;
      }

      const unique = [];
      const seen = new Set();

      for (const match of parsed) {
        const key = matchKey(match);

        if (!seen.has(key)) {
          seen.add(key);
          unique.push(match);
        }
      }

      setMatches(unique);

      alert(
        `Generated ${unique.length} matches successfully.`
      );
    } catch (error) {
      console.error(
        "Generation error:",
        error
      );

      alert(
        `Generation error: ${
          error?.message ||
          "Unknown error"
        }`
      );
    } finally {
      setGenerating(false);
    }
  }

  /* =======================================================
     REMOVE MATCH
  ======================================================= */

  function removeMatchByKey(key) {
    setMatches((prev) =>
      prev.filter(
        (match) =>
          matchKey(match) !== key
      )
    );
  }

  /* =======================================================
     CLEAR ALL
  ======================================================= */

  function clearAll() {
    setMatches([]);
    setManualText("");
    setSearch("");
    setCountryFilter("all");
    setLeagueFilter("all");
    setDateFilter("");
    setTimeFilter("");
  }

  /* =======================================================
     SAVE ALL
  ======================================================= */

  async function saveAllMatches() {
    if (!matches.length) {
      alert(
        "Hakuna matches za kuhifadhi."
      );
      return;
    }

    const loginUser = getAppLoginUser();

    if (!loginUser) {
      alert("Tafadhali login kwanza.");
      return;
    }

    setSaving(true);

    try {
      const matchesRef =
        collection(
          db,
          MATCHES_COLLECTION
        );

      const existingSnap =
        await getDocs(matchesRef);

      const existingKeys = new Set();

      existingSnap.forEach(
        (docSnap) => {
          const data =
            docSnap.data();

          existingKeys.add(
            matchKey({
              A: data.A,
              B: data.B,
              date: data.date,
              time: data.time,
              league: data.league,
            })
          );
        }
      );

      let added = 0;
      let skipped = 0;
      let failed = 0;

      for (const match of matches) {
        try {
          if (
            !match.date ||
            !match.time
          ) {
            skipped++;
            continue;
          }

          const normalTime =
            normaliseTime(match.time);

          if (!normalTime) {
            skipped++;
            continue;
          }

          if (
            !isValidISODate(
              match.date
            )
          ) {
            skipped++;
            continue;
          }

          const key = matchKey({
            ...match,
            time: normalTime,
          });

          if (existingKeys.has(key)) {
            skipped++;
            continue;
          }

          const payload = {
            A: String(
              match.A || ""
            ).trim(),

            B: String(
              match.B || ""
            ).trim(),

            date: match.date,

            time: normalTime,

            country:
              match.country || "",

            league:
              match.league || "",

            timezone:
              match.timezone ||
              TIMEZONE,

            logoA:
              match.logoA || "",

            logoB:
              match.logoB || "",

            leagueLogo:
              match.leagueLogo || "",

            odds: {
              home:
                match.odds?.home ??
                null,

              draw:
                match.odds?.draw ??
                null,

              away:
                match.odds?.away ??
                null,
            },

            correctScores:
              Array.isArray(
                match.correctScores
              )
                ? match.correctScores.map(
                    (score) => ({
                      score:
                        score.score ??
                        "",
                      odds:
                        safeNumber(
                          score.odds
                        ),
                    })
                  )
                : getCorrectScores(),

            source: "manual",

            createdBy:
              loginUser.uid ||
              loginUser.username ||
              localStorage.getItem("username") ||
              "unknown",

            createdAt:
              serverTimestamp(),

            updatedAt:
              serverTimestamp(),
          };

          await addDoc(
            matchesRef,
            payload
          );

          existingKeys.add(key);

          added++;
        } catch (error) {
          failed++;

          console.error(
            "Failed to save match:",
            match,
            error
          );
        }
      }

      alert(
        `SAVE COMPLETE\n\n` +
          `Added: ${added}\n` +
          `Skipped: ${skipped}\n` +
          `Failed: ${failed}`
      );
    } catch (error) {
      console.error(
        "Save error:",
        error
      );

      alert(
        `Save error: ${
          error?.message ||
          "Unknown Firestore error"
        }`
      );
    } finally {
      setSaving(false);
    }
  }

  /* =======================================================
     UI
  ======================================================= */

  return (
    <div className="all-add-matches">
    <AdminQuickLinks />


      {/* HEADER */}

      <div className="all-add-matches-header">
        <div>
          <h1>
            Add Matches
          </h1>

          <p>
            Paste betting data and
            generate Firebase-ready
            matches automatically.
          </p>
        </div>

        <div className="matches-count">
          {matches.length} Matches
        </div>
      </div>

      {/* PASTE CARD */}

      <div className="paste-card">
        <div className="paste-header">
          <div>
            <h2>
              Paste Betting Data
            </h2>

            <p>
              Bandika betting dump
              yote hapa.
            </p>
          </div>
        </div>

        <textarea
          className="matches-textarea"
          value={manualText}
          onChange={(event) =>
            setManualText(
              event.target.value
            )
          }
          placeholder={`Mfano:

Portugal - U23 Liga Next Gen
+6
SL Benfica U23
Academico Viseu U23
2.723.452.15
Today 19:00

Czechia - MSFL
+94
SK Hranice
TJ Unie Hlubina
2.653.502.18
Tomorrow 20:30`}
        />

        <div className="input-actions">
          <button
            type="button"
            className="generate-btn"
            onClick={parseAllMatches}
            disabled={generating}
          >
            {generating
              ? "Generating..."
              : "⚡ Generate Matches"}
          </button>

          <button
            type="button"
            className="clear-btn"
            onClick={() =>
              setManualText("")
            }
            disabled={generating}
          >
            Clear Text
          </button>
        </div>
      </div>

      {/* FILTERS */}

      {matches.length > 0 && (
        <div className="filters-card">

          <div className="filter-group search-group">
            <label>
              Search
            </label>

            <input
              type="text"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Team, league..."
            />
          </div>

          <div className="filter-group">
            <label>
              Country
            </label>

            <select
              value={countryFilter}
              onChange={(event) => {
                setCountryFilter(
                  event.target.value
                );

                setLeagueFilter("all");
              }}
            >
              {countries.map(
                (country) => (
                  <option
                    key={country}
                    value={country}
                  >
                    {country === "all"
                      ? "All Countries"
                      : country}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="filter-group">
            <label>
              League
            </label>

            <select
              value={leagueFilter}
              onChange={(event) =>
                setLeagueFilter(
                  event.target.value
                )
              }
            >
              {leagues.map(
                (league) => (
                  <option
                    key={league}
                    value={league}
                  >
                    {league === "all"
                      ? "All Leagues"
                      : league}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="filter-group">
            <label>
              Date
            </label>

            <input
              type="date"
              value={dateFilter}
              onChange={(event) =>
                setDateFilter(
                  event.target.value
                )
              }
            />
          </div>

          <div className="filter-group">
            <label>
              Time
            </label>

            <input
              type="time"
              value={timeFilter}
              onChange={(event) =>
                setTimeFilter(
                  event.target.value
                )
              }
            />
          </div>
        </div>
      )}

      {/* ACTION BAR */}

      {matches.length > 0 && (
        <div className="matches-action-bar">

          <div>
            <strong>
              Showing{" "}
              {filteredMatches.length}
            </strong>{" "}
            of{" "}
            {matches.length}
          </div>

          <div className="action-buttons">

            <button
              type="button"
              className="save-btn"
              onClick={saveAllMatches}
              disabled={
                saving ||
                !matches.length
              }
            >
              {saving
                ? "Saving..."
                : `💾 Save All (${matches.length})`}
            </button>

            <button
              type="button"
              className="clear-all-btn"
              onClick={clearAll}
              disabled={saving}
            >
              Clear All
            </button>

          </div>
        </div>
      )}

      {/* GENERATED MATCHES */}

      <div className="generated-matches">

        {filteredMatches.length === 0 &&
          matches.length > 0 && (
            <div className="empty-state">
              Hakuna match inayolingana
              na filters.
            </div>
          )}

        {matches.length === 0 &&
          !generating && (
            <div className="empty-state">

              <div className="empty-icon">
                ⚽
              </div>

              <h3>
                No matches yet
              </h3>

              <p>
                Paste betting data
                hapo juu kisha
                bonyeza Generate.
              </p>

            </div>
          )}

        {filteredMatches.map(
          (match) => {
            const key =
              matchKey(match);

            return (
                  <div
                className="match-card"
                key={key}
              >

                {/* TOP */}

                <div className="match-card-top">

                  <div className="league-info">

                    <span className="country">
                      {match.country}
                    </span>

                    <span className="separator">
                      •
                    </span>

                    <span className="league">
                      {match.league}
                    </span>

                  </div>

                  <button
                    type="button"
                    className="remove-match"
                    onClick={() =>
                      removeMatchByKey(
                        key
                      )
                    }
                    disabled={saving}
                  >
                    ×
                  </button>

                </div>

                {/* TEAMS */}

                <div className="match-main">

                  <div className="team home-team">

                    <strong>
                      {match.A}
                    </strong>

                    <small>
                      Home
                    </small>

                  </div>

                  <div className="match-time">

                    <strong>
                      {match.time ||
                        "--:--"}
                    </strong>

                    <span>
                      {match.date}
                    </span>

                  </div>

                  <div className="team away-team">

                    <strong>
                      {match.B}
                    </strong>

                    <small>
                      Away
                    </small>

                  </div>

                </div>

                {/* ODDS */}

                <div className="odds-row">

                  <div className="odd">
                    <span>
                      1
                    </span>

                    <strong>
                      {match.odds?.home ??
                        "-"}
                    </strong>
                  </div>

                  <div className="odd">
                    <span>
                      X
                    </span>

                    <strong>
                      {match.odds?.draw ??
                        "-"}
                    </strong>
                  </div>

                  <div className="odd">
                    <span>
                      2
                    </span>

                    <strong>
                      {match.odds?.away ??
                        "-"}
                    </strong>
                  </div>

                </div>

                {/* SCHEMA */}

                <div className="match-schema">

                  <span>
                    📅 Date:{" "}
                    <strong>
                      {match.date}
                    </strong>
                  </span>

                  <span>
                    🕐 Time:{" "}
                    <strong>
                      {match.time}
                    </strong>
                  </span>

                  <span>
                    🌍 {match.timezone}
                  </span>

                </div>

              </div>
            );
          }
        )}

      </div>
    </div>
  );
}